import React from 'react';
import Icon from '../AppIcon';

const StepProgressIndicator = ({ 
  currentStep = 1, 
  totalSteps = 3,
  stepLabels = ['Formulaire', 'Aperçu PDF', 'Confirmation'],
  completedSteps = [],
  className = ''
}) => {
  const getStepStatus = (stepIndex) => {
    if (completedSteps?.includes(stepIndex)) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    if (status === 'completed') return 'CheckCircle2';
    if (status === 'current') return 'Circle';
    return 'Circle';
  };

  return (
    <div className={`step-progress-indicator ${className}`}>
      <div className="step-progress-container">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const status = getStepStatus(stepNumber);
          const isLast = stepNumber === totalSteps;

          return (
            <React.Fragment key={stepNumber}>
              <div className="step-item">
                <div className={`step-icon-wrapper step-${status}`}>
                  <Icon 
                    name={getStepIcon(stepNumber)}
                    size={24}
                    className={`step-icon ${status === 'completed' ? 'text-success' : status === 'current' ? 'text-primary' : 'text-muted-foreground'}`}
                  />
                </div>
                <div className="step-content">
                  <span className={`step-label ${status === 'current' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {stepLabels?.[index]}
                  </span>
                  <span className="step-number caption">
                    Étape {stepNumber} sur {totalSteps}
                  </span>
                </div>
              </div>
              {!isLast && (
                <div className={`step-connector ${completedSteps?.includes(stepNumber) ? 'step-connector-completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <style jsx>{`
        .step-progress-indicator {
          position: sticky;
          top: 0;
          z-index: 100;
          background: rgba(10, 10, 10, 0.9);
          backdrop-filter: blur(40px) saturate(180%);
          -webkit-backdrop-filter: blur(40px) saturate(180%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          padding: 1.5rem 0;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        }

        .step-progress-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 0;
        }

        .step-icon-wrapper {
          flex-shrink: 0;
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 400ms cubic-bezier(0.25, 0.1, 0.25, 1);
          backdrop-filter: blur(20px);
        }

        .step-icon-wrapper.step-completed {
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.3);
          box-shadow: 0 0 20px rgba(16, 185, 129, 0.2);
        }

        .step-icon-wrapper.step-current {
          background: rgba(74, 144, 226, 0.12);
          border: 1px solid rgba(74, 144, 226, 0.4);
          box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15), 0 0 30px rgba(74, 144, 226, 0.3);
        }

        .step-icon-wrapper.step-upcoming {
          background: rgba(42, 42, 42, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }

        .step-content {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          min-width: 0;
        }

        .step-label {
          font-size: 0.9375rem;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .step-number {
          font-size: 0.8125rem;
          color: var(--color-muted-foreground);
        }

        .step-connector {
          flex-shrink: 0;
          width: 3rem;
          height: 2px;
          background: rgba(255, 255, 255, 0.06);
          transition: all 400ms cubic-bezier(0.25, 0.1, 0.25, 1);
        }

        .step-connector-completed {
          background: linear-gradient(90deg, rgba(16, 185, 129, 0.6), rgba(16, 185, 129, 0.3));
          box-shadow: 0 0 12px rgba(16, 185, 129, 0.3);
        }

        @media (max-width: 768px) {
          .step-progress-container {
            padding: 0 1rem;
            gap: 0.5rem;
          }

          .step-item {
            gap: 0.5rem;
          }

          .step-icon-wrapper {
            width: 2.25rem;
            height: 2.25rem;
          }

          .step-content {
            display: none;
          }

          .step-connector {
            width: 1.5rem;
          }
        }

        @media (max-width: 640px) {
          .step-progress-indicator {
            padding: 1rem 0;
          }

          .step-progress-container {
            gap: 0.375rem;
          }

          .step-connector {
            width: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default StepProgressIndicator;